import React, { useState, useEffect, useRef, startTransition } from 'react';

import { Link, useNavigate, useRouteLoaderData } from '@remix-run/react';
import StoreSelector from '~/components/store-selector';
import BrandLogo from '~/components/brand-logo';
import { useCart } from '~/context/cart-context';
import { useStore } from '~/context/store-context';
import LoginForm from './login-form';
import AgeGate from '~/components/age-gate';
import SearchBar from '~/components/search-bar';
// Lazy load MobileSidebar to reduce initial bundle size
const MobileSidebar = React.lazy(() => import('~/components/mobile-side-bar'));
import { klaviyo, trackLogout } from '~/utils/analytics';
import { getResponsiveRegions } from '~/hooks/use-regions';
import SafeSuspense from '~/components/safe-suspense';
import { useHideOnScroll } from '~/hooks/use-hide-on-scroll';
// Import GTranslate directly for instant loading
import GTranslate from './google-translate';

// Keep the loading outline identical to the points control during session validation.
const pointsControlClassName =
  'font-magdaMono block w-full truncate rounded-xl border border-white px-1.5 py-1 text-center text-xs tabular-nums text-white';

// Utility to get a cookie by name.
function getCookie(name: string): string | null {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// Custom hook: calls handler if click happens outside the given ref.
function useOnClickOutside(
  ref: React.RefObject<HTMLElement>,
  handler: (event: MouseEvent) => void,
): void {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      handler(event);
    };
    document.addEventListener('click', listener);
    return () => document.removeEventListener('click', listener);
  }, [ref, handler]);
}

export default function Header(): JSX.Element {
  // Search input toggle — local to Header so toggling doesn't re-render
  // the layout, Footer, SideCart, GlobalProductModal, etc.
  const [showMobileSearch, setShowMobileSearch] = useState<boolean>(false);
  const { toggleCart, cartCount } = useCart();
  const { selectedStore } = useStore();
  const navigate = useNavigate();

  // Authentication and user data.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const rootData = useRouteLoaderData<{ hasSessionHint: boolean }>('root');
  const [isAuthPending, setIsAuthPending] = useState(rootData?.hasSessionHint ?? false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userFirstName, setUserFirstName] = useState('Your');
  const [userLastName, setUserLastName] = useState('Account');
  const [userEmail, setUserEmail] = useState('');
  const [loyaltyPoints, setLoyaltyPoints] = useState<number | null>(null);
  const [showLoyaltyDropdown, setShowLoyaltyDropdown] = useState<boolean>(false);

  const loyaltyRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(loyaltyRef, () => setShowLoyaltyDropdown(false));
  const mobileLanguageRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(mobileLanguageRef, () => setShowMobileLanguageDropdown(false));

  // Handle brand selection tracking
  const handleBrandSelection = (brandName: string) => {
    klaviyo.track('Brand Selected', {
      brand_name: brandName,
      source: 'header_dropdown',
      timestamp: new Date().toISOString(),
    });
  };
  // Regional store organization with shorter names for better UX
  const storeRegions = {
    'Bay Area': [
      { name: 'Antioch', link: '/dispensary-locations/antioch-dispensary' },
      { name: 'Daly City', link: '/dispensary-locations/daly-city-dispensary' },
      { name: 'Marina', link: '/dispensary-locations/marina-dispensary' },
      { name: 'Patterson', link: '/dispensary-locations/patterson-dispensary' },
      { name: 'Watsonville', link: '/dispensary-locations/watsonville-dispensary' },
    ],
    'Los Angeles': [
      { name: 'Bellflower', link: '/dispensary-locations/bellflower-dispensary' },
      { name: 'Belmont Shore LB', link: '/dispensary-locations/belmont-shore-dispensary' },
      { name: 'Cherry Ave LB', link: '/dispensary-locations/cherry-ave-long-beach-dispensary' },
      { name: 'Downtown LB', link: '/dispensary-locations/downtown-long-beach-dispensary' },
      { name: 'El Monte', link: '/dispensary-locations/el-monte-dispensary' },
      { name: 'Figueroa Ave LA', link: '/dispensary-locations/figueroa-los-angeles-dispensary' },
      { name: 'Florence Ave LA', link: '/dispensary-locations/florence-los-angeles-dispensary' },
      { name: 'Hawthorne', link: '/dispensary-locations/hawthorne-dispensary' },
      { name: 'Lynwood', link: '/dispensary-locations/lynwood-dispensary' },
      { name: 'Mid City LA', link: '/dispensary-locations/mid-city-los-angeles-dispensary' },
      { name: 'Normandie Ave LA', link: '/dispensary-locations/normandie-los-angeles-dispensary' },
      { name: 'Pomona', link: '/dispensary-locations/pomona-dispensary' },
      { name: 'Retro Row LB', link: '/dispensary-locations/retro-row-long-beach-dispensary' },
      { name: 'Silver Lake', link: '/dispensary-locations/silver-lake-dispensary' },
      { name: 'Van Nuys', link: '/dispensary-locations/van-nuys-dispensary' },
    ],
    'Orange County': [
      { name: 'Costa Mesa', link: '/dispensary-locations/costa-mesa-dispensary' },
      { name: 'OC3 Santa Ana', link: '/dispensary-locations/oc3-santa-ana-dispensary' },
      { name: 'Planet Catalyst', link: '/dispensary-locations/planet-catalyst-orange-county' },
      { name: 'Santa Ana', link: '/dispensary-locations/santa-ana-dispensary' },
      { name: 'Stanton', link: '/dispensary-locations/stanton-dispensary' },
    ],
    'Inland Empire': [
      { name: 'Hemet', link: '/dispensary-locations/hemet-dispensary' },
      { name: 'Lancaster', link: '/dispensary-locations/lancaster-dispensary' },
      { name: 'Moreno Valley', link: '/dispensary-locations/moreno-valley-dispensary' },
      { name: 'Palm Desert', link: '/dispensary-locations/palm-desert-dispensary' },
      { name: 'San Bernardino', link: '/dispensary-locations/san-bernardino-dispensary' },
    ],
    'Central Coast': [
      { name: 'Grover Beach', link: '/dispensary-locations/grover-beach-dispensary' },
      { name: 'Oxnard', link: '/dispensary-locations/oxnard-dispensary' },
    ],
    'San Diego': [{ name: 'San Diego', link: '/dispensary-locations/san-diego-dispensary' }],
  };

  const onClose = () => {
    setIsLocationsMenuOpen(false);
    setIsBrandsMenuOpen(false);
    setIsDealsMenuOpen(false);
    setIsShopMenuOpen(false);
  };

  // Mobile sidebar and search states.
  const [showMobileSidebar, setShowMobileSidebar] = useState<boolean>(false);

  // New state for LoginForm modal.
  const [showLoginForm, setShowLoginForm] = useState<boolean>(false);
  // Header brands list (top N only for compact dropdown)
  type BrandItem = { name: string; slug: string; count: number };
  const [headerBrands, setHeaderBrands] = useState<Array<BrandItem>>([]);
  const [isBrandsMenuOpen, setIsBrandsMenuOpen] = useState(false);
  const [showAllHeaderBrands, setShowAllHeaderBrands] = useState(false);
  const brandsMenuRef = useRef<HTMLLIElement>(null);
  useOnClickOutside(brandsMenuRef, () => setIsBrandsMenuOpen(false));
  const [isLocationsMenuOpen, setIsLocationsMenuOpen] = useState(false);
  const locationsMenuRef = useRef<HTMLLIElement>(null);
  useOnClickOutside(locationsMenuRef, () => setIsLocationsMenuOpen(false));

  // Ref for sticky container to track dynamic bottom position
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  // New dropdown states
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const shopMenuRef = useRef<HTMLLIElement>(null);
  useOnClickOutside(shopMenuRef, () => setIsShopMenuOpen(false));

  const [isDealsMenuOpen, setIsDealsMenuOpen] = useState(false);
  const dealsMenuRef = useRef<HTMLLIElement>(null);
  useOnClickOutside(dealsMenuRef, () => setIsDealsMenuOpen(false));

  // Trigger-button refs so keyboard focus can return to the button when a menu closes
  const shopBtnRef = useRef<HTMLButtonElement>(null);
  const dealsBtnRef = useRef<HTMLButtonElement>(null);
  const locationsBtnRef = useRef<HTMLButtonElement>(null);
  const brandsBtnRef = useRef<HTMLButtonElement>(null);

  /**
   * DEALS MENU CONFIGURATION
   * ========================
   *
   * HOW IT WORKS:
   * - If there's only 1 deal item: Renders as a simple clickable link (no dropdown)
   * - If there are 2+ deal items: Automatically renders as a dropdown menu
   *
   * TO ADD NEW DEALS LINKS:
   * 1. Add new items to the dealsItems array below
   * 2. Format: { label: 'Display Name', href: '/url-path' }
   * 3. The system will automatically switch to dropdown mode when you add a 2nd item
   *
   * EXAMPLE - Adding multiple deals:
   * ```typescript
   * const dealsItems = [
   *   { label: 'All Deals', href: '/deals' },
   *   { label: 'Weekly Specials', href: '/deals/weekly' },
   *   { label: 'Flash Sales', href: '/deals/flash' },
   *   { label: 'Member Deals', href: '/deals/members' },
   * ];
   * ```
   *
   * NOTE: No other code changes needed! Just update this array and the UI will adapt automatically.
   */
  const dealsItems = [{ label: 'All Deals', href: '/deals' }];
  const hasMultipleDeals = dealsItems.length > 1;

  // State for category dropdown toggles - all open by default when shop menu opens
  const [categoryDropdowns, setCategoryDropdowns] = useState({
    flower: true,
    prerolls: true,
    vapes: true,
    concentrates: true,
    edibles: true,
    drinks: true,
    topicals: true,
    tinctures: true,
    merch: true,
    other: true,
  });

  // State for nested "Other" category dropdowns
  const [otherNestedDropdowns, setOtherNestedDropdowns] = useState({
    edibles: false,
    drinks: false,
    topicals: false,
    tinctures: false,
    merch: false,
  });

  // State for nested region dropdowns in locations menu - open by default for desktop
  const [regionDropdowns, setRegionDropdowns] = useState({
    'Long Beach': true,
    'Los Angeles': true,
    'Orange County': true,
    'Inland Empire / San Diego': true,
    'Central Coast': true,
    'Northern California': true,
    'Other Locations': true,
  });

  // State for dynamic subtypes data
  const [categorySubtypes, setCategorySubtypes] = useState<Record<string, any[]>>({});
  const [subtypesLoaded, setSubtypesLoaded] = useState(false);

  // State to trigger re-render on window resize for responsive categories
  const [windowWidth, setWindowWidth] = useState(1280); // Default to XL for SSR
  const [isClient, setIsClient] = useState(false);
  const [isRegionsHydrated, setIsRegionsHydrated] = useState(false);
  const [showMobileLanguageDropdown, setShowMobileLanguageDropdown] = useState(false);
  const [gtranslateLoaded, setGtranslateLoaded] = useState(false);

  // No need for custom language list or helper function - GTranslate handles everything

  // Check when GTranslate select is loaded - only after client hydration
  useEffect(() => {
    // Only run on client after hydration
    if (typeof window === 'undefined') return;

    let intervalId: NodeJS.Timeout | null = null;
    let isCleanedUp = false;

    const checkGTranslate = () => {
      if (isCleanedUp) return;

      const select = document.querySelector('.gtranslate_wrapper select') as HTMLSelectElement;
      if (select) {
        // Wrap state update in startTransition to prevent hydration errors
        startTransition(() => {
          setGtranslateLoaded(true);
        });
        // Clear interval once loaded
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Small delay to ensure hydration is complete before checking
    const timeout = setTimeout(() => {
      checkGTranslate();
      // Set up interval to check periodically
      intervalId = setInterval(checkGTranslate, 100);
    }, 200);

    return () => {
      isCleanedUp = true;
      clearTimeout(timeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Add aria-label, id, and name to GTranslate select for keyboard accessibility
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const addAttributesToSelects = () => {
      // Get ALL select elements including .gt_selector class (GTranslate sometimes creates duplicates)
      const selects = document.querySelectorAll(
        'aside .single-gtranslate .gtranslate_wrapper select, aside .single-gtranslate .gtranslate_wrapper .gt_selector',
      ) as NodeListOf<HTMLSelectElement>;

      selects.forEach((select, index) => {
        // Skip if already has both id and name
        if (select.id && select.name) return;

        // Add id and name for accessibility (use index to make unique if multiple)
        if (!select.id) {
          select.id = index === 0 ? 'language-selector' : `language-selector-${index}`;
        }
        if (!select.name) {
          select.name = index === 0 ? 'language-selector' : `language-selector-${index}`;
        }

        // Add aria-label for desktop
        if (window.innerWidth >= 1024) {
          select.setAttribute(
            'aria-label',
            'Language selector. Press Enter or the down arrow to open the dropdown menu.',
          );
        }
      });
    };

    // Run immediately
    addAttributesToSelects();

    // Use MutationObserver to catch selects as they're added to DOM
    const observer = new MutationObserver(() => {
      addAttributesToSelects();
    });

    // Observe the GTranslate wrapper for changes
    const wrapper = document.querySelector('aside .single-gtranslate .gtranslate_wrapper');
    if (wrapper) {
      observer.observe(wrapper, {
        childList: true,
        subtree: true,
      });
    }

    // Also check periodically as backup (stop after 5 seconds)
    const interval = setInterval(addAttributesToSelects, 200);
    const stopInterval = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(stopInterval);
    };
  }, []); // Run on mount, don't wait for gtranslateLoaded

  // Open the language selector with Enter/Space using the native picker API.
  // Works on desktop and mobile; a synthetic mousedown does not open a native <select>.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      if (!(active instanceof HTMLSelectElement)) return;
      // Only the GTranslate language select, not other selects on the page
      if (!active.closest('.single-gtranslate, .gtranslate_wrapper')) return;

      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        const el = active as HTMLSelectElement & { showPicker?: () => void };
        if (typeof el.showPicker === 'function') {
          e.preventDefault();
          try {
            el.showPicker();
          } catch {
            // showPicker throws without a user gesture / when unsupported — ignore
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Keep keyboard focus inside the open desktop dropdown: Escape closes and returns
  // focus to the trigger; Tab / Shift+Tab cycle within the panel instead of escaping
  // into unrelated page content further down the DOM.
  useEffect(() => {
    const openPanelId = isShopMenuOpen
      ? 'desktop-shop-submenu'
      : isLocationsMenuOpen
        ? 'desktop-locations-submenu'
        : isBrandsMenuOpen
          ? 'desktop-brands-submenu'
          : null;
    if (!openPanelId && !isDealsMenuOpen) return;

    const closeAndReturnFocus = () => {
      if (isShopMenuOpen) {
        setIsShopMenuOpen(false);
        shopBtnRef.current?.focus();
      } else if (isLocationsMenuOpen) {
        setIsLocationsMenuOpen(false);
        locationsBtnRef.current?.focus();
      } else if (isBrandsMenuOpen) {
        setIsBrandsMenuOpen(false);
        brandsBtnRef.current?.focus();
      } else if (isDealsMenuOpen) {
        setIsDealsMenuOpen(false);
        dealsBtnRef.current?.focus();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAndReturnFocus();
        return;
      }
      if (e.key === 'Tab' && openPanelId) {
        const panel = document.getElementById(openPanelId);
        if (!panel) return;
        const focusable = Array.from(
          panel.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        // Only act while focus is genuinely inside this panel — never yank focus away
        // from another component (e.g. an open modal) that legitimately holds it.
        if (!panel.contains(active)) return;
        if (e.shiftKey) {
          if (active === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isShopMenuOpen, isLocationsMenuOpen, isBrandsMenuOpen, isDealsMenuOpen]);

  // When a full-width dropdown opens, move focus into its panel so keyboard users
  // land in the submenu instead of tabbing past it to the next nav item.
  useEffect(() => {
    const openPanelId = isShopMenuOpen
      ? 'desktop-shop-submenu'
      : isLocationsMenuOpen
        ? 'desktop-locations-submenu'
        : isBrandsMenuOpen
          ? 'desktop-brands-submenu'
          : null;
    if (!openPanelId) return;

    const raf = requestAnimationFrame(() => {
      const panel = document.getElementById(openPanelId);
      const first = panel?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isShopMenuOpen, isLocationsMenuOpen, isBrandsMenuOpen]);

  // Toggle individual category dropdown
  const toggleCategoryDropdown = (category: string) => {
    setCategoryDropdowns((prev) => ({
      ...prev,
      [category]: !prev[category as keyof typeof prev],
    }));
  };

  // Category data structure - Responsive columns based on screen size
  const categoryData: Record<
    string,
    { name: string; slug: string; subcategories?: Array<{ name: string; slug: string }> }
  > = {
    flower: { name: 'Flower', slug: 'flower' },
    prerolls: { name: 'Prerolls', slug: 'prerolls' },
    vapes: { name: 'Vapes', slug: 'vapes' },
    concentrates: { name: 'Concentrates', slug: 'concentrates' },
    edibles: { name: 'Edibles', slug: 'edibles' },
    drinks: { name: 'Drinks', slug: 'beverages' },
    topicals: { name: 'Topicals', slug: 'topicals' },
    tinctures: { name: 'Tinctures', slug: 'tinctures' },
    merch: { name: 'Merch', slug: 'merch' },
    pill: { name: 'Pill', slug: 'pill' },
    other: {
      name: 'Other',
      slug: 'other',
      subcategories: [
        { name: 'Topicals', slug: 'topicals' },
        { name: 'Tinctures', slug: 'tinctures' },
        { name: 'Merch', slug: 'merch' },
        { name: 'Pill', slug: 'pill' },
      ],
    },
  };

  // Responsive category display logic
  // Correct order: Flower, Prerolls, Vapes, Concentrates, Edibles, Drinks, Topicals, Tinctures, Merch
  const getVisibleCategories = (): Array<
    [string, { name: string; slug: string; subcategories?: Array<{ name: string; slug: string }> }]
  > => {
    // During SSR or before client hydration, always show XL layout to prevent hydration mismatch
    if (!isClient) {
      return [
        ['flower', categoryData.flower],
        ['prerolls', categoryData.prerolls],
        ['vapes', categoryData.vapes],
        ['concentrates', categoryData.concentrates],
        ['edibles', categoryData.edibles],
        ['drinks', categoryData.drinks],
        [
          'other',
          {
            name: 'Other',
            slug: 'other',
            subcategories: [
              { name: 'Topicals', slug: 'topicals' },
              { name: 'Tinctures', slug: 'tinctures' },
              { name: 'Merch', slug: 'merch' },
              { name: 'Pill', slug: 'pill' },
            ],
          },
        ],
      ];
    }

    const width = windowWidth;

    if (width >= 1280) {
      // XL: Show 7 columns (Flower, Prerolls, Vapes, Concentrates, Edibles, Drinks, Other)
      return [
        ['flower', categoryData.flower],
        ['prerolls', categoryData.prerolls],
        ['vapes', categoryData.vapes],
        ['concentrates', categoryData.concentrates],
        ['edibles', categoryData.edibles],
        ['drinks', categoryData.drinks],
        [
          'other',
          {
            name: 'Other',
            slug: 'other',
            subcategories: [
              { name: 'Topicals', slug: 'topicals' },
              { name: 'Tinctures', slug: 'tinctures' },
              { name: 'Merch', slug: 'merch' },
              { name: 'Pill', slug: 'pill' },
            ],
          },
        ],
      ];
    } else if (width >= 1024) {
      // LG: Show 5 columns (Flower, Prerolls, Vapes, Concentrates, Other)
      // Other contains: Edibles, Drinks, Topicals, Tinctures, Merch (in correct order)
      return [
        ['flower', categoryData.flower],
        ['prerolls', categoryData.prerolls],
        ['vapes', categoryData.vapes],
        ['concentrates', categoryData.concentrates],
        [
          'other',
          {
            name: 'Other',
            slug: 'other',
            subcategories: [
              { name: 'Edibles', slug: 'edibles' },
              { name: 'Drinks', slug: 'beverages' },
              { name: 'Topicals', slug: 'topicals' },
              { name: 'Tinctures', slug: 'tinctures' },
              { name: 'Merch', slug: 'merch' },
              { name: 'Pill', slug: 'pill' },
            ],
          },
        ],
      ];
    } else {
      // MD: Show 4 columns (Flower, Prerolls, Vapes, Other)
      // Other contains: Concentrates, Edibles, Drinks, Topicals, Tinctures, Merch (in correct order)
      return [
        ['flower', categoryData.flower],
        ['prerolls', categoryData.prerolls],
        ['vapes', categoryData.vapes],
        [
          'other',
          {
            name: 'Other',
            slug: 'other',
            subcategories: [
              { name: 'Concentrates', slug: 'concentrates' },
              { name: 'Edibles', slug: 'edibles' },
              { name: 'Drinks', slug: 'beverages' },
              { name: 'Topicals', slug: 'topicals' },
              { name: 'Tinctures', slug: 'tinctures' },
              { name: 'Merch', slug: 'merch' },
              { name: 'Pill', slug: 'pill' },
            ],
          },
        ],
      ];
    }
  };

  // Use the responsive regions function without smart prioritization (static order)
  const getVisibleRegions = (): Array<
    [
      string,
      (
        | Array<{ name: string; link: string }>
        | { name: string; locations: Array<{ name: string; link: string }> }
      ),
    ]
  > => {
    return getResponsiveRegions(windowWidth, isClient, null, isRegionsHydrated);
  };

  // Mobile category data - All categories including those grouped under "Other" on desktop
  const mobileCategoryData = {
    flower: { name: 'Flower', slug: 'flower' },
    prerolls: { name: 'Prerolls', slug: 'prerolls' },
    vapes: { name: 'Vapes', slug: 'vapes' },
    concentrates: { name: 'Concentrates', slug: 'concentrates' },
    edibles: { name: 'Edibles', slug: 'edibles' },
    drinks: { name: 'Drinks', slug: 'beverages' },
    topicals: { name: 'Topicals', slug: 'topicals' },
    tinctures: { name: 'Tinctures', slug: 'tinctures' },
    merch: { name: 'Merch', slug: 'merch' },
    pill: { name: 'Pill', slug: 'pill' },
  };

  // Cache key for localStorage - store-specific to ensure correct data per store
  const SUBTYPES_CACHE_KEY = `catalyst_subtypes_cache_v4_${selectedStore || 'default'}`;
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  // Handle client-side hydration and window resize for responsive categories
  useEffect(() => {
    // Set initial client-side window width and mark as hydrated
    setIsClient(true);
    setWindowWidth(window.innerWidth);

    // Delay region hydration to prevent hydration mismatches
    startTransition(() => {
      setIsRegionsHydrated(true);
    });

    const handleResize = () => {
      startTransition(() => {
        setWindowWidth(window.innerWidth);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide nav bar on scroll down, show on scroll up (like Amazon)
  const hidden = useHideOnScroll({
    disabled:
      showMobileSearch ||
      showMobileSidebar ||
      isShopMenuOpen ||
      isBrandsMenuOpen ||
      isLocationsMenuOpen ||
      isDealsMenuOpen,
    headerRef: stickyContainerRef,
  });

  // Focus the search input after it becomes visible and notify the carousel
  // to pause its auto-advance so the smooth-scroll animation doesn't block typing.
  useEffect(() => {
    if (showMobileSearch) {
      requestAnimationFrame(() => {
        document.getElementById('mobile-search')?.focus();
      });
    }
    window.dispatchEvent(new CustomEvent('search-toggle', { detail: { open: showMobileSearch } }));
  }, [showMobileSearch]);

  // Close all menus when nav hides
  useEffect(() => {
    if (hidden) {
      setIsShopMenuOpen(false);
      setIsBrandsMenuOpen(false);
      setIsLocationsMenuOpen(false);
      setIsDealsMenuOpen(false);
      setShowLoyaltyDropdown(false);
    }
  }, [hidden]);

  // Track header bottom position for InstantSearchResults overlay positioning.
  // Only uses ResizeObserver (fires when sticky container height changes,
  // e.g. search wrapper expanding) and resize/orientationchange events.
  // NO scroll listener — getBoundingClientRect() on every scroll frame was
  // forcing synchronous layout reflows and causing 400ms+ INP spikes.
  useEffect(() => {
    if (!stickyContainerRef.current || typeof window === 'undefined') return;

    const el = stickyContainerRef.current;

    const updateHeaderBottom = () => {
      // Stable stuck height for desktop sidebars; no per-scroll measurements.
      document.documentElement.style.setProperty('--sticky-header-height', `${el.offsetHeight}px`);
      const searchWrapper = document.getElementById('searchwrapmobile');
      let bottom: number;
      if (searchWrapper && searchWrapper.offsetHeight > 0) {
        bottom = Math.max(0, searchWrapper.getBoundingClientRect().bottom);
      } else {
        bottom = Math.max(0, el.getBoundingClientRect().bottom);
      }
      document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
    };

    updateHeaderBottom();

    // ResizeObserver handles height changes (search wrapper open/close, etc.)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeaderBottom);
      resizeObserver.observe(el);
    }

    // IntersectionObserver on the elements above the sticky container (tax bar
    // and store selector) so --header-bottom updates as they scroll in/out of
    // view. This avoids a scroll listener (which caused 400ms+ INP spikes from
    // forced synchronous layout reflows on every scroll frame).
    let intersectionObserver: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      const thresholds = Array.from({ length: 11 }, (_, i) => i / 10);
      intersectionObserver = new IntersectionObserver(updateHeaderBottom, {
        threshold: thresholds,
      });
      const addressEntry = document.getElementById('addressentry');
      if (addressEntry) {
        intersectionObserver.observe(addressEntry);
        // Also observe the tax bar (aside) above the store selector
        const taxBar = addressEntry.previousElementSibling;
        if (taxBar) intersectionObserver.observe(taxBar);
      }
    }

    // Resize/orientation only — not scroll
    window.addEventListener('resize', updateHeaderBottom);
    window.addEventListener('orientationchange', updateHeaderBottom);

    return () => {
      window.removeEventListener('resize', updateHeaderBottom);
      window.removeEventListener('orientationchange', updateHeaderBottom);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
    };
  }, [hidden, showMobileSearch]); // Re-run when nav hides/shows or search wrapper toggles

  // Load subtypes from cache or fetch fresh
  const loadSubtypes = async () => {
    // Check if we have data for all categories, not just if we've loaded before
    const expectedCategories = Object.keys(categoryData);
    const loadedCategories = Object.keys(categorySubtypes);
    const hasAllCategories = expectedCategories.every((cat) => loadedCategories.includes(cat));

    if (subtypesLoaded && hasAllCategories) return;

    try {
      // Try to load from cache first (only on client side)
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(SUBTYPES_CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const isExpired = Date.now() - timestamp > CACHE_DURATION;

          if (!isExpired) {
            startTransition(() => {
              setCategorySubtypes(data);
              setSubtypesLoaded(true);
            });
            return;
          }
        }
      }

      // Fetch fresh data - all categories in parallel using general facets API
      const promises = Object.entries(categoryData).map(async ([key, category]) => {
        const url = `/api/facets?category=${category.slug}&facet=subtype${selectedStore ? `&store=${selectedStore}` : ''}`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          return { key, subtypes: data.subtype || [] };
        } else {
          console.error(
            `Failed to fetch subtypes for ${key} (${category.slug}):`,
            response.status,
            response.statusText,
          );
          return { key, subtypes: [] };
        }
      });

      const results = await Promise.all(promises);
      const subtypesMap: Record<string, any[]> = {};

      results.forEach(({ key, subtypes }) => {
        subtypesMap[key] = subtypes;
      });

      startTransition(() => {
        setCategorySubtypes(subtypesMap);
        setSubtypesLoaded(true);
      });

      // Cache the results (only on client side)
      if (typeof window !== 'undefined') {
        localStorage.setItem(
          SUBTYPES_CACHE_KEY,
          JSON.stringify({
            data: subtypesMap,
            timestamp: Date.now(),
          }),
        );
      }
    } catch (error) {
      console.error('Error fetching subtypes:', error);
    }
  };

  // Preload on hover for instant experience
  const handleShopHover = () => {
    loadSubtypes();
  };

  // Load immediately when shop menu opens (fallback)
  useEffect(() => {
    if (isShopMenuOpen) {
      loadSubtypes();
    }
  }, [isShopMenuOpen]);

  // Also preload subtypes on component mount for better UX
  // Load subtypes when component mounts or store changes
  useEffect(() => {
    // Clear old cache entries for different stores to prevent bloat
    if (typeof window !== 'undefined' && selectedStore) {
      const oldCacheKeys = [
        'catalyst_subtypes_cache',
        'catalyst_subtypes_cache_v2',
        'catalyst_subtypes_cache_v3',
      ];
      oldCacheKeys.forEach((key) => localStorage.removeItem(key));

      // Also clear cache entries for other stores (keep only current store)
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('catalyst_subtypes_cache_v4_') && !key.includes(selectedStore)) {
          localStorage.removeItem(key);
        }
      });
    }

    loadSubtypes();
  }, [selectedStore]); // Reload when store changes

  // Load brands for header menu
  useEffect(() => {
    const loadBrands = async () => {
      try {
        // Use count-based ordering (popularity by product count)
        const res = await fetch(`/api/brands?limit=1000&order=count`);
        if (!res.ok) return;
        const data = await res.json();
        startTransition(() => {
          setHeaderBrands(data.brands || []);
        });
      } catch (e) {
        console.error('Error loading header brands:', e);
      }
    };
    loadBrands();
  }, []);

  // Simple brand filtering - remove Stiiizy and sort alphabetically
  const filteredBrands = headerBrands.filter(
    (b) => b.name.toUpperCase() !== 'STIIIZY' && b.name.toUpperCase() !== 'STIIZY',
  );

  // Only verified sessions or completed logins enable the account UI.
  useEffect(() => {
    const login = () => { setIsLoggedIn(true); setIsAuthPending(false); setSessionExpired(false); };
    const logout = () => {
      setIsAuthPending(false);
      setIsLoggedIn(false);
      setShowLoyaltyDropdown(false);
      setUserFirstName('Your'); setUserLastName('Account'); setUserEmail('');
      setLoyaltyPoints(0);
    };
    const expired = () => setSessionExpired(true);
    // Cookie hints only keep the loading state visible; validation still gates account access.
    const hasSessionHint = getCookie('catalystUserId') || getCookie('access_token') || getCookie('refresh_token');
    let hasStoredUser = false;
    try { hasStoredUser = !!localStorage.getItem('catalystUserId'); } catch { /* Storage may be blocked. */ }
    if (!hasSessionHint && !hasStoredUser) setIsAuthPending(false);
    window.addEventListener('user:login', login);
    window.addEventListener('auth:validated', login);
    window.addEventListener('user:logout', logout);
    window.addEventListener('auth:expired', expired);
    return () => {
      window.removeEventListener('user:login', login);
      window.removeEventListener('auth:validated', login);
      window.removeEventListener('user:logout', logout);
      window.removeEventListener('auth:expired', expired);
    };
  }, []);

  // Retrieve user info from cookies on mount.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const firstName = getCookie('first_name');
      const lastName = getCookie('last_name');
      const email = getCookie('email');
      if (firstName && lastName) {
        setUserFirstName(firstName);
        setUserLastName(lastName);
      }
      if (email) {
        setUserEmail(decodeURIComponent(email));
      }
    }
  }, []);

  // Fetch loyalty points.
  useEffect(() => {
    const maxRetries = 3;
    let attempt = 0;
    const fetchLoyaltyPoints = async () => {
      try {
        const res = await fetch('/api/alpine/');
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        const data = await res.json();
        setLoyaltyPoints(data.points != null ? data.points : 0);
      } catch (err) {
        console.error('Error fetching loyalty points:', err);
        if (attempt < maxRetries) {
          attempt++;
          setTimeout(fetchLoyaltyPoints, 1000);
        } else {
          setLoyaltyPoints(0);
        }
      }
    };
    fetchLoyaltyPoints();
  }, []);

  // Logout handler.
  const handleLogout = () => {
    // 🔥 Enhanced Analytics - Track logout events
    klaviyo.logout();
    trackLogout();

    document.cookie = 'catalystUserId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setIsLoggedIn(false);
    window.dispatchEvent(new CustomEvent('user:logout'));
  };

  return (
    <>
      <AgeGate />
      {sessionExpired && (
        <div role="status" className="bg-amber-50 px-4 py-3 text-center text-sm text-neutral-900">
          Please sign in again to access your account and member pricing. Your cart is still saved.
          <LoginForm />
        </div>
      )}
      {/* Top Notification Bar - Normal flow, scrolls away */}
      <aside
        className="bg-catalystblue relative flex h-11 items-center justify-center px-2 py-2"
        role="complementary"
        aria-label="Tax information"
      >
        <div className="relative mx-auto flex w-full max-w-[106rem] items-center justify-center">
          {/* Tax notice - responsive with text truncation on small screens */}
          <span className="font-poppins lg:truncate-none max-w-[calc(100%-80px)] truncate text-center text-sm font-bold text-white md:max-w-[calc(100%-200px)] md:text-base lg:max-w-none">
            ALL TAXES INCLUDED IN MENU PRICING
          </span>
          {/* Language Selector - Right side */}
          <div ref={mobileLanguageRef} className="absolute right-0 z-20 flex items-center gap-2">
            {/* Desktop: Show "Translate:" label */}
            <span className="hidden text-sm font-medium text-white lg:inline">Translate:</span>

            {/* Mobile: Globe icon - visual only, select is positioned behind it */}
            <div className="relative flex items-center lg:hidden [&:has(:focus-visible)]:rounded-full [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-[#ffd100] [&:has(:focus-visible)]:ring-offset-2">
              <div className="pointer-events-none flex items-center">
                <svg
                  className="h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
            </div>

            {/* Desktop: Show placeholder until GTranslate loads */}
            {!gtranslateLoaded && (
              <div className="absolute right-0 z-20 hidden lg:block">
                <select
                  id="language-selector-placeholder"
                  name="language-selector-placeholder"
                  className="header-language-placeholder"
                  defaultValue="en"
                  disabled
                  aria-label="Language selector loading"
                >
                  <option value="en">English</option>
                </select>
              </div>
            )}

            {/* Single GTranslate instance for both mobile and desktop */}
            <div
              className={`single-gtranslate ${!gtranslateLoaded ? 'pointer-events-none opacity-0' : ''}`}
            >
              <GTranslate />
            </div>
          </div>
        </div>
      </aside>
      {/* Top Bar: Store Selector / Address - Normal flow, scrolls away */}
      <section
        id="addressentry"
        className="z-[100] flex w-full items-center justify-center bg-white px-4 py-2"
        role="banner"
        aria-label="Store selection"
      >
        <div className="flex cursor-pointer items-center font-semibold text-black">
          <span className="font-helvetica-neue mx-2 text-gray-600">Shopping At:</span>
          <div className="text-catalystblue mr-2">
            <StoreSelector
              onStoreSelect={() => {
                setShowMobileSidebar(false);
              }}
              onModalOpen={() => {
                setShowMobileSidebar(false);
              }}
            />
          </div>
          <button
            aria-label="Change selected store"
            className="transform transition-transform duration-300"
          ></button>
        </div>
      </section>

      {/* Sticky wrapper - nav sticks to top when scrolling */}
      <div ref={stickyContainerRef} className="sticky top-0 z-50">
        {/* Navigation Bar - animated on scroll */}
        <nav
          className="bg-catalystblue py-2.5 text-white"
          role="navigation"
          aria-label="Main navigation"
          style={{
            transform: hidden ? 'translateY(-100%)' : 'none',
            transition: 'transform 300ms ease-out',
            pointerEvents: hidden ? 'none' : 'auto',
          }}
        >
          <div className="mx-auto grid max-w-[106rem] grid-cols-[1fr_auto_1fr] items-center px-3 md:grid-cols-[auto_1fr_auto] lg:px-6">
            {/* Left Section */}
            <div className="left-sec flex items-center gap-2">
              <button
                type="button"
                className="min-h-[40px] items-center justify-center p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] md:hidden md:p-2"
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                aria-expanded={showMobileSidebar}
                aria-controls="mobile-menu-drawer"
                aria-label={showMobileSidebar ? 'Close navigation menu' : 'Open navigation menu'}
              >
                {showMobileSidebar ? (
                  <img
                    width="24"
                    height="24"
                    src="/assets/icons/white-close-icon.svg"
                    alt=""
                    role="presentation"
                    aria-hidden="true"
                    className="size-6"
                    decoding="async"
                  />
                ) : (
                  <img
                    width="24"
                    height="24"
                    src="/assets/images/hamburger.svg"
                    alt=""
                    role="presentation"
                    aria-hidden="true"
                    className="size-6"
                    decoding="async"
                  />
                )}
              </button>
              {/* search button to show search input */}
              <button
                type="button"
                className="min-h-[40px] min-w-[40px] cursor-pointer p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] md:hidden md:p-2"
                onClick={() => {
                  setShowMobileSidebar(false);
                  setShowMobileSearch((prev) => !prev);
                }}
                aria-expanded={showMobileSearch}
                aria-controls="searchwrapmobile"
                aria-label="Toggle search"
              >
                <svg
                  width="24"
                  height="24"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  aria-hidden="true"
                  role="presentation"
                >
                  <path
                    fill="white"
                    d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z"
                  />
                </svg>
              </button>
              {/* Logo - Desktop only */}
              <div className="logo-wrapper hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] md:block">
                <Link to="/" className="">
                  <img
                    src="/logos/Catalyst-Logo.webp"
                    alt="Catalyst Cannabis home"
                    className="aspect-[183/40] h-8 lg:h-10"
                    decoding="sync"
                    {...({ fetchpriority: 'high' } as any)}
                  />
                </Link>
              </div>
            </div>
            {/* Navigation Section - Desktop only */}
            <div className="hidden justify-center md:flex">
              <ul className="font-poppins hidden list-none flex-nowrap gap-2 text-base leading-6 antialiased md:flex lg:gap-6 xl:gap-8 2xl:gap-10">
                <li ref={shopMenuRef} className="menu-item">
                  <button
                    ref={shopBtnRef}
                    type="button"
                    aria-haspopup="true"
                    aria-controls="desktop-shop-submenu"
                    aria-expanded={isShopMenuOpen}
                    onClick={() => {
                      setShowMobileSidebar(false);
                      setIsShopMenuOpen((v) => !v);
                      setIsDealsMenuOpen(false);
                      setIsBrandsMenuOpen(false);
                      setIsLocationsMenuOpen(false);
                    }}
                    onMouseEnter={handleShopHover}
                    className="inline-flex items-center gap-1 font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                  >
                    <span>Shop</span>
                    <svg
                      className={`h-3.5 w-3.5 transform transition-transform ${isShopMenuOpen ? 'rotate-180' : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </li>
                {/* 
                  DEALS MENU RENDERING LOGIC
                  ==========================
                  - If hasMultipleDeals === true: Renders dropdown button with menu
                  - If hasMultipleDeals === false: Renders simple link (no dropdown)
                  - To add more deals, update the dealsItems array above (see documentation)
                */}
                {hasMultipleDeals ? (
                  <li ref={dealsMenuRef} className="menu-item relative">
                    <button
                      ref={dealsBtnRef}
                      type="button"
                      aria-haspopup="true"
                      aria-controls="desktop-deals-submenu"
                      aria-expanded={isDealsMenuOpen}
                      onClick={() => {
                        setShowMobileSidebar(false);
                        setIsDealsMenuOpen((v: boolean) => !v);
                        setIsShopMenuOpen(false);
                        setIsBrandsMenuOpen(false);
                        setIsLocationsMenuOpen(false);
                      }}
                      className="inline-flex items-center gap-1 font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                    >
                      <span>Deals</span>
                      <svg
                        className={`h-3.5 w-3.5 transform transition-transform ${isDealsMenuOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isDealsMenuOpen && (
                      <ul
                        id="desktop-deals-submenu"
                        className="absolute top-full left-0 z-30 mt-2 min-w-[240px] rounded-lg border border-gray-100 bg-white px-0 py-2 shadow-lg"
                      >
                        {dealsItems.map((deal) => (
                          <li key={deal.href}>
                            <Link
                              to={deal.href}
                              onClick={() => setIsDealsMenuOpen(false)}
                              className="hover:text-catalystblue focus-visible:text-catalystblue block w-full px-5 py-2 text-left text-base text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:bg-[#ffd100]"
                            >
                              {deal.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ) : (
                  // Single deal item - renders as simple link (no dropdown)
                  <li className="menu-item">
                    <Link
                      to={dealsItems[0].href}
                      className="font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                    >
                      Deals
                    </Link>
                  </li>
                )}
                <li className="menu-item">
                  <Link
                    to="/blog"
                    className="font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                  >
                    Blog
                  </Link>
                </li>
                <li className="menu-item">
                  <Link
                    to="/events"
                    className="font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                  >
                    Events
                  </Link>
                </li>
                <li ref={locationsMenuRef} className="menu-item">
                  <button
                    ref={locationsBtnRef}
                    type="button"
                    aria-haspopup="true"
                    aria-controls="desktop-locations-submenu"
                    aria-expanded={isLocationsMenuOpen}
                    onClick={() => {
                      // Prevent opening before regions are hydrated to avoid errors
                      if (!isRegionsHydrated) return;

                      setShowMobileSidebar(false);
                      setIsLocationsMenuOpen((v) => !v);
                      setIsDealsMenuOpen(false);
                      setIsBrandsMenuOpen(false);
                      setIsShopMenuOpen(false);
                    }}
                    className="inline-flex cursor-pointer items-center gap-1 font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                  >
                    <span>Locations</span>
                    <svg
                      className={`h-3.5 w-3.5 transform transition-transform ${isLocationsMenuOpen ? 'rotate-180' : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </li>
                <li ref={brandsMenuRef} className="menu-item">
                  <button
                    ref={brandsBtnRef}
                    type="button"
                    aria-haspopup="true"
                    aria-controls="desktop-brands-submenu"
                    aria-expanded={isBrandsMenuOpen}
                    onClick={() => {
                      setShowMobileSidebar(false);
                      setIsBrandsMenuOpen((v) => !v);
                      setIsDealsMenuOpen(false);
                      setIsLocationsMenuOpen(false);
                      setIsShopMenuOpen(false);
                    }}
                    className="inline-flex items-center gap-1 font-normal text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                  >
                    <span>Brands</span>
                    <svg
                      className={`h-3.5 w-3.5 transform transition-transform ${isBrandsMenuOpen ? 'rotate-180' : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </li>
              </ul>
            </div>
            {/* Mobile Center Section: Logo */}
            <div className="flex justify-center md:hidden">
              <div className="logo-wrapper text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]">
                <Link to="/" className="">
                  <img
                    src="/logos/Catalyst-Logo.webp"
                    alt="Catalyst Cannabis home"
                    className="mx-auto h-[22px] w-auto md:h-[50px]"
                    width="336"
                    height="73"
                    style={{ aspectRatio: '336/73' }}
                    decoding="sync"
                    {...({ fetchpriority: 'high' } as any)}
                  />
                </Link>
              </div>
            </div>
            {/* Right Section */}
            <div className="right-sec flex items-center justify-end">
              <ul className="flex list-none flex-wrap items-center justify-end gap-0.5 md:gap-1 lg:gap-[10px]">
                {/* Search Icon for lg-xl screens */}
                <li className="hidden md:block xl:hidden">
                  <button
                    type="button"
                    className="min-h-[40px] min-w-[40px] cursor-pointer p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                    onClick={() => {
                      setShowMobileSearch((prev) => !prev);
                    }}
                    aria-expanded={showMobileSearch}
                    aria-controls="searchwrapmobile"
                    aria-label="Toggle search"
                  >
                    <svg
                      width="24"
                      height="24"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <path
                        fill="white"
                        d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z"
                      />
                    </svg>
                  </button>
                </li>
                <li className="w-0 overflow-hidden transition-all duration-200 xl:mr-4 xl:w-[348px] xl:overflow-visible">
                  <div className="relative mx-auto w-full max-w-md">
                    <SearchBar
                      id="desktop-search"
                      className="font-helvetica-neue min-h-[44px] w-full rounded-full bg-white/10 px-4 py-2 text-white placeholder-gray-200 focus:outline-none focus-visible:bg-white/20 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                      onSubmit={(q) => navigate(`/shop?search=${encodeURIComponent(q)}`)}
                      onSearchClose={() => setShowMobileSearch(false)}
                    />
                  </div>
                </li>

                {isAuthPending ? (
                  <li className="header-account-slot" aria-busy="true" aria-label="Loading account">
                    <span className={pointsControlClassName} aria-hidden="true">
                      … Pts
                    </span>
                  </li>
                ) : isLoggedIn ? (
                  <li className="header-account-slot">
                    <div ref={loyaltyRef} className="relative w-full">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLoyaltyDropdown(!showLoyaltyDropdown);
                        }}
                        className={`${pointsControlClassName} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]`}
                      >
                        {loyaltyPoints == null ? '…' : loyaltyPoints} Pts
                      </button>
                      {showLoyaltyDropdown && (
                        <div
                          className="absolute right-0 z-50 mt-1 w-56 rounded-lg border border-neutral-200 bg-white shadow-lg transition"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-4">
                            <div className="mb-3 flex items-center space-x-3">
                              <div>
                                <p className="text-sm font-medium text-neutral-900">
                                  {userFirstName} {userLastName}
                                </p>
                                <p className="text-xs text-neutral-500">{userEmail}</p>
                              </div>
                            </div>
                            <div className="border-y border-neutral-200 py-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-neutral-600">Loyalty Points</span>
                                <span className="text-sm font-medium text-neutral-900">
                                  {loyaltyPoints == null ? 'Loading…' : loyaltyPoints}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLogout();
                              }}
                              className="mt-3 flex w-full items-center text-sm text-neutral-700"
                            >
                              <i className="fa-solid fa-right-from-bracket"></i>
                              <span>Logout</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ) : (
                  <li className="header-account-slot">
                    <LoginForm onLoginClick={() => setShowMobileSidebar(false)} />
                  </li>
                )}
                <li className="flex">
                  <button
                    onClick={() => {
                      setShowMobileSidebar(false);
                      toggleCart();
                    }}
                    className="relative min-h-[40px] min-w-[40px] p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] md:p-2"
                    aria-label={`Shopping cart with ${cartCount} items`}
                    aria-expanded="false"
                    aria-controls="side-cart"
                  >
                    <svg
                      width="30"
                      height="25"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 512"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <path
                        d="M64 16C64 7.2 71.2 0 80 0l37.9 0c20.6 0 38.6 13 45.3 32l440.4 0c20.8 0 36.1 19.6 31 39.8L592.6 239.5C585.5 268 559.9 288 530.5 288L216 288l7.9 38.4c3 14.9 16.1 25.6 31.4 25.6L560 352c8.8 0 16 7.2 16 16s-7.2 16-16 16l-304.8 0c-30.4 0-56.6-21.4-62.7-51.2l-58.9-288C132 37.3 125.5 32 117.9 32L80 32c-8.8 0-16-7.2-16-16zM530.5 256c14.7 0 27.5-10 31-24.2L603.5 64 170.1 64l39.3 192 321.1 0zM256 480a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm0-80a56 56 0 1 1 0 112 56 56 0 1 1 0-112zm280 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm-80 0a56 56 0 1 1 112 0 56 56 0 1 1 -112 0zM16 128l96 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-96 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64l112 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L16 224c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64l128 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L16 288c-8.8 0-16-7.2-16-16s7.2-16 16-16z"
                        fill="white"
                      ></path>
                    </svg>
                    <span
                      className="text-catalystblue absolute top-2 right-2 flex h-5 w-5 -translate-y-1/2 translate-x-1/2 transform items-center justify-center rounded-full bg-[#ffd100] text-xs font-normal"
                      aria-hidden="true"
                    >
                      {cartCount}
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          {/* Hidden help text for search inputs - contained within nav landmark */}
          <div id="search-help" className="sr-only">
            Type to search products. Results will appear as you type. Use arrow keys to navigate
            results.
          </div>
        </nav>

        {/* Mobile Search Box — absolutely positioned so it overlays page content
            instead of pushing it down (avoids re-laying out the product grid).
            Uses CSS Grid 0fr/1fr so the SearchBar stays in the layout tree when
            collapsed — no cold-start cost when first opened. */}
        <div
          id="searchwrapmobile"
          className="bg-catalystblue absolute right-0 left-0 xl:hidden"
          style={{
            top: '100%',
            zIndex: 40,
            display: 'grid',
            gridTemplateRows: showMobileSearch ? '1fr' : '0fr',
          }}
          role="search"
        >
          <div className="overflow-hidden">
            <div
              className={`relative mx-auto w-full max-w-md px-4 ${showMobileSearch ? 'py-2' : ''}`}
            >
              <SearchBar
                id="mobile-search"
                className="min-h-[44px] w-full rounded-full bg-white/10 px-4 py-2 text-white placeholder-gray-200 focus:outline-none focus-visible:bg-white/20 focus-visible:ring-2 focus-visible:ring-white"
                onSubmit={(q) => {
                  navigate(`/shop?search=${encodeURIComponent(q)}`);
                  setShowMobileSearch(false);
                }}
                onSearchClose={() => setShowMobileSearch(false)}
                tabIndex={showMobileSearch ? 0 : -1}
              />
            </div>
          </div>
        </div>

        {/* Desktop Dropdowns - Positioned relative to header container */}
        {/* Shop Categories Dropdown - Full Width with Expandable Subcategories */}
        <div
          id="desktop-shop-submenu"
          className={`font-poppins antialiased absolute top-full left-0 z-50 hidden max-h-[calc(100dvh-160px)] w-full transform overflow-y-auto overscroll-contain bg-white transition-all duration-300 ease-out md:block ${
            isShopMenuOpen
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-4 opacity-0'
          }`}
          {...(isShopMenuOpen ? {} : ({ inert: '' } as any))}
          role="navigation"
          aria-label="Shop categories"
        >
          <div className="mx-auto flex max-w-[105rem] px-8 py-6">
            {getVisibleCategories().map(([categoryKey, category], index) => {
              const isLastColumn = index === getVisibleCategories().length - 1;
              const isExpanded = categoryDropdowns[categoryKey as keyof typeof categoryDropdowns];
              const subtypes = categorySubtypes[categoryKey] || [];

              return (
                <div
                  key={categoryKey}
                  className={`flex min-w-0 flex-1 flex-col px-4 first:pl-0 last:pr-0 ${
                    !isLastColumn ? 'border-r border-[#D9D9D9]' : ''
                  }`}
                >
                  {/* Category Header with Dropdown Arrow */}
                  <button
                    onClick={() => toggleCategoryDropdown(categoryKey)}
                    className="mb-3 flex min-h-9 items-center justify-between gap-3 border-b border-[#D9D9D9] pb-2 text-left text-sm leading-[22px] font-medium text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                    aria-expanded={isExpanded}
                    aria-controls={`${categoryKey}-subcategories`}
                    role="button"
                  >
                    <span>{category.name}</span>
                    <svg
                      className={`h-4 w-4 transform transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Subcategories - Collapsible */}
                  <div
                    id={`${categoryKey}-subcategories`}
                    className={`overflow-hidden transition-all duration-300 ${
                      isExpanded ? 'max-h-[48rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      {categoryKey === 'other' ? (
                        // Special handling for "Other" category - show only top-level category links (no subcategories)
                        <>
                          {category.subcategories?.map((subcategory) => {
                            const subcategoryKey =
                              subcategory.slug === 'beverages' ? 'drinks' : subcategory.slug;
                            const hasSubtypes = categorySubtypes[subcategoryKey]?.length > 0;
                            const isNestedExpanded =
                              otherNestedDropdowns[
                                subcategoryKey as keyof typeof otherNestedDropdowns
                              ];

                            return (
                              // Show only top-level category link (no subcategories)
                              <Link
                                key={subcategory.slug}
                                to={`/category/${subcategory.slug}`}
                                onClick={() => setIsShopMenuOpen(false)}
                                className="block py-0.5 text-sm leading-[22px] whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                              >
                                {subcategory.name}
                              </Link>
                            );
                          })}
                          {/* Apparel & Accessories - External merch site */}
                          <a
                            href="https://catalyst-merch.com/?utm_source=catalyst-cannabis&utm_medium=internal&utm_campaign=merch&utm_content=apparel_accessories_nav"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsShopMenuOpen(false)}
                            className="flex items-center gap-1 py-0.5 text-sm leading-[22px] whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                            aria-label="Apparel & Accessories - Opens catalyst-merch.com in a new tab"
                          >
                            <span>Apparel & Accessories</span>
                            <svg
                              className="h-3 w-3 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </a>
                        </>
                      ) : subtypes.length > 0 ? (
                        // Regular categories with dynamic subtypes
                        <>
                          {subtypes.map((subtype) => (
                            <Link
                              key={subtype.slug}
                              to={`/shop?subtype=${encodeURIComponent(subtype.originalName || subtype.name)}`}
                              onClick={() => setIsShopMenuOpen(false)}
                              className="block py-0.5 text-sm leading-[22px] whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                            >
                              {subtype.name}
                            </Link>
                          ))}
                          <Link
                            to={`/category/${category.slug}`}
                            onClick={() => setIsShopMenuOpen(false)}
                            className="mt-2 block py-0.5 text-sm leading-[22px] font-medium whitespace-normal text-[#264B9A] hover:text-[#1a3a7a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                          >
                            Shop All {category.name}
                          </Link>
                        </>
                      ) : (
                        // Categories without subtypes - show "Shop All" link
                        <Link
                          to={`/category/${category.slug}`}
                          onClick={() => setIsShopMenuOpen(false)}
                          className="mt-2 block py-0.5 text-sm leading-[22px] font-medium whitespace-normal text-[#264B9A] hover:text-[#1a3a7a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                        >
                          Shop All {category.name}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Blue bottom section - full screen width */}
          <div className="bg-catalystblue w-screen py-3">
            <div className="mx-auto max-w-[106rem] text-center">
              <Link
                to="/shop"
                onClick={() => setIsShopMenuOpen(false)}
                className="font-medium text-white hover:text-[#ffd100] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
              >
                Shop All Products
              </Link>
            </div>
          </div>
        </div>

        {/* Locations Dropdown */}
        <div
          id="desktop-locations-submenu"
          className={`font-poppins antialiased absolute top-full left-0 z-50 hidden max-h-[calc(100dvh-160px)] w-full transform overflow-y-auto overscroll-contain bg-white transition-all duration-300 ease-out md:block ${
            isLocationsMenuOpen
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-4 opacity-0'
          }`}
          {...(isLocationsMenuOpen ? {} : ({ inert: '' } as any))}
          role="navigation"
          aria-label="Dispensary locations"
        >
          <div className="mx-auto flex max-w-[105rem] px-8 py-6">
            {getVisibleRegions().map(([regionName, regionData], index) => {
              const isExpanded = regionDropdowns[regionName as keyof typeof regionDropdowns];
              const isLastColumn = index === getVisibleRegions().length - 1;

              // Handle "Other Locations" special case
              const isOtherLocations = regionName === 'Other Locations';
              let stores = isOtherLocations
                ? (regionData as { name: string; locations: Array<{ name: string; link: string }> })
                    .locations
                : (regionData as Array<{ name: string; link: string }>);

              // Alphabetize Other Locations
              if (isOtherLocations) {
                stores = [...stores].sort((a, b) => a.name.localeCompare(b.name));
              }

              return (
                <div
                  key={regionName}
                  className={`flex min-w-0 flex-1 flex-col px-4 first:pl-0 last:pr-0 ${
                    !isLastColumn ? 'border-r border-[#D9D9D9]' : ''
                  }`}
                >
                  {/* Region Header with Dropdown Arrow */}
                  <button
                    onClick={() =>
                      setRegionDropdowns((prev) => ({
                        ...prev,
                        [regionName]: !prev[regionName as keyof typeof prev],
                      }))
                    }
                    className="mb-3 flex min-h-14 items-center justify-between gap-3 border-b border-[#D9D9D9] pb-2 text-left text-sm leading-[22px] font-medium whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                    aria-expanded={isExpanded}
                    aria-controls={`${regionName.replace(/\s+/g, '-').toLowerCase()}-stores`}
                    role="button"
                  >
                    <span>{regionName}</span>
                    <svg
                      className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Region Stores */}
                  <div
                    id={`${regionName.replace(/\s+/g, '-').toLowerCase()}-stores`}
                    className={`overflow-hidden transition-all duration-300 ${
                      isExpanded ? 'max-h-[48rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      {stores.map((store) => (
                        <Link
                          key={store.name}
                          to={store.link}
                          onClick={() => setIsLocationsMenuOpen(false)}
                          className="block py-0.5 text-sm leading-[22px] whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                        >
                          {store.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Blue bottom section - full screen width */}
          <div className="w-screen border-t border-[#D9D9D9] bg-[#264B9A]">
            <div className="mx-auto max-w-[106rem] py-3">
              <Link
                to="/dispensary-locations"
                onClick={() => setIsLocationsMenuOpen(false)}
                className="block text-center text-sm font-medium text-white hover:text-[#ffd100] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none focus-visible:ring-offset-[#264B9A]"
              >
                View All Locations
              </Link>
            </div>
          </div>
        </div>

        {/* Brands Dropdown */}
        <div
          id="desktop-brands-submenu"
          className={`font-poppins antialiased absolute top-full left-0 z-50 hidden max-h-[calc(100dvh-160px)] w-full transform overflow-y-auto overscroll-contain bg-white transition-all duration-300 ease-out md:block ${
            isBrandsMenuOpen
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-4 opacity-0'
          }`}
          {...(isBrandsMenuOpen ? {} : ({ inert: '' } as any))}
          role="navigation"
          aria-label="Cannabis brands"
        >
          <div className="mx-auto max-w-[105rem] px-8 py-6">
            {(() => {
              // Priority brands that should always be included in top 32
              const priorityBrands = ['CATALYST', 'CAT4', 'DELTA WHIP', 'MJUICE', 'WHT ASH'];

              // Filter out Stiiizy (already done in filteredBrands)
              // Brands are already sorted by popularity (count) from API with order=count

              // Find priority brands that are actually available
              const priorityPresent = filteredBrands.filter((b) =>
                priorityBrands.includes(b.name.toUpperCase()),
              );

              // Get other brands (excluding priority brands)
              const otherBrands = filteredBrands.filter(
                (b) => !priorityBrands.includes(b.name.toUpperCase()),
              );

              // Take top brands by popularity (API already sorted by count)
              // Include all priority brands + fill remaining slots with popular brands (32 total)
              const remainingSlots = Math.max(0, 32 - priorityPresent.length);
              const topOtherBrands = otherBrands.slice(0, remainingSlots);

              // Combine priority brands + top popular brands, then sort alphabetically
              const top32Brands = [...priorityPresent, ...topOtherBrands].sort((a, b) =>
                a.name.localeCompare(b.name),
              );

              // All brands in alphabetical order (for "View More Brands")
              const allBrandsAlphabetical = [...filteredBrands].sort((a, b) =>
                a.name.localeCompare(b.name),
              );

              const brands = showAllHeaderBrands
                ? allBrandsAlphabetical // All brands alphabetical when expanded
                : top32Brands; // Top 32 (priority + popular) sorted alphabetically

              // Create column-based distribution (alphabetical down each column)
              // 4 columns for initial 32 brands, dynamic for expanded view
              const columnCount = 4;
              const itemsPerColumn = Math.ceil(brands.length / columnCount);

              return (
                <div className="grid grid-cols-4 gap-x-6">
                  {Array.from({ length: columnCount }, (_, columnIndex) => {
                    const startIndex = columnIndex * itemsPerColumn;
                    const endIndex = Math.min(startIndex + itemsPerColumn, brands.length);
                    const columnBrands = brands.slice(startIndex, endIndex);
                    const isLastColumn = columnIndex === columnCount - 1;

                    if (columnBrands.length === 0) return null;

                    return (
                      <div
                        key={columnIndex}
                        className={`flex-1 ${!isLastColumn ? 'border-r border-[#D9D9D9] pr-4' : ''}`}
                      >
                        {columnBrands.map((b) => (
                          <div key={b.slug} className="py-0.5">
                            <Link
                              to={`/brand/${b.slug}`}
                              onClick={() => {
                                handleBrandSelection(b.name);
                                setIsBrandsMenuOpen(false);
                              }}
                              className="flex min-h-12 items-center gap-3 rounded-md py-1 text-sm leading-[22px] font-medium whitespace-normal text-black hover:text-[#264B9A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                            >
                              <BrandLogo name={b.name} />
                              <span className="min-w-0 break-words">
                                {b.name
                                  .toLowerCase()
                                  .replace(/(?:^|\s)\w/g, (l: string) => l.toUpperCase())}
                              </span>
                            </Link>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* View More Brands Toggle - Separate section */}
          {(() => {
            const totalBrands = filteredBrands.length;
            const hasMoreBrands = totalBrands > 32;

            return hasMoreBrands ? (
              <div className="mx-auto max-w-[105rem] px-8 pb-5">
                <button
                  onClick={() => setShowAllHeaderBrands(!showAllHeaderBrands)}
                  className="text-sm font-medium text-[#264B9A] hover:text-[#1a4080] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2"
                  role="button"
                  aria-expanded={showAllHeaderBrands}
                >
                  {showAllHeaderBrands ? 'Show Fewer Brands' : 'View More Brands'}
                </button>
              </div>
            ) : null;
          })()}

          {/* Blue bottom section - full screen width */}
          <div className="w-screen border-t border-[#D9D9D9] bg-[#264B9A]">
            <div className="mx-auto max-w-[106rem] py-3">
              <Link
                to="/brand"
                onClick={() => setIsBrandsMenuOpen(false)}
                className="block text-center text-sm font-medium text-white hover:text-[#ffd100] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none focus-visible:ring-offset-[#264B9A]"
              >
                Shop All Brands
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Component - Uses flexbox internally to fill remaining viewport height */}
        {/* CRITICAL FIX: Always render Suspense boundary to prevent hydration errors */}
        {/* Conditionally rendering Suspense causes React error #421 */}
        <SafeSuspense
          fallback={
            <div
              className="fixed right-0 left-0 z-[9999] w-full bg-white md:hidden"
              style={{
                top: 'var(--header-bottom, 0px)',
                bottom: 0,
                display: 'none',
              }}
            />
          }
        >
          {isClient ? (
            <MobileSidebar isOpen={showMobileSidebar} onClose={() => setShowMobileSidebar(false)} />
          ) : null}
        </SafeSuspense>
      </div>

      {/* Hidden help text for search inputs - moved inside nav landmark */}
    </>
  );
}
